import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_CONFIG } from '../api/config'; // For fallback data structure
import type { DistributionData as OriginalDistributionData } from '../types/distribution'; // REVISED IMPORT

// Define DistributionTier locally as it's not exported from the types file
interface DistributionTier {
  percent: string;
  total: string;
}

// Interface for data coming from Supabase 'distribution_stats' table (snake_case)
interface DbDistributionStats {
  id?: number;
  supply: number | null;
  tier_1_25: DistributionTier | null; 
  tier_26_50: DistributionTier | null;
  tier_51_75: DistributionTier | null;
  tier_76_100: DistributionTier | null;
  tier_101_plus: DistributionTier | null;
  last_updated_at?: string | null;
}

// Helper to transform DB data to the OriginalDistributionData format
const transformDbToDistributionData = (dbData: DbDistributionStats | null): OriginalDistributionData => {
  if (!dbData || dbData.supply === null) { // If no data or supply is missing, return fallback
    return API_CONFIG.FALLBACK.DISTRIBUTION;
  }
  return {
    supply: dbData.supply,
    t_1_25: dbData.tier_1_25 ?? API_CONFIG.FALLBACK.DISTRIBUTION.t_1_25,
    t_26_50: dbData.tier_26_50 ?? API_CONFIG.FALLBACK.DISTRIBUTION.t_26_50,
    t_51_75: dbData.tier_51_75 ?? API_CONFIG.FALLBACK.DISTRIBUTION.t_51_75,
    t_76_100: dbData.tier_76_100 ?? API_CONFIG.FALLBACK.DISTRIBUTION.t_76_100,
    t_101plus: dbData.tier_101_plus ?? API_CONFIG.FALLBACK.DISTRIBUTION.t_101plus,
  };
};

export function useDistribution() {
  const [data, setData] = useState<OriginalDistributionData>(() => transformDbToDistributionData(null)); // Initialize with fallback
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchInitialDistributionData() {
      if (!mounted) return;
      setLoading(true);
      setError(null);
      try {
        const { data: dbData, error: dbError } = await supabase
          .from('distribution_stats')
          .select('*')
          .eq('id', 1)
          .single<DbDistributionStats>();

        if (!mounted) return;

        if (dbError) {
          console.error('Error fetching initial distribution data from Supabase:', dbError);
          setData(API_CONFIG.FALLBACK.DISTRIBUTION); // Use fallback on error
          setError(new Error('Failed to fetch initial distribution data: ' + dbError.message));
        } else if (dbData) {
          setData(transformDbToDistributionData(dbData));
        } else {
           setData(API_CONFIG.FALLBACK.DISTRIBUTION); // No data found, use fallback
           setError(new Error('No distribution data found in database.'));
        }
      } catch (err) {
        if (!mounted) return;
        console.error('Catch block error fetching initial distribution data:', err);
        setData(API_CONFIG.FALLBACK.DISTRIBUTION);
        setError(err instanceof Error ? err : new Error('An unexpected error occurred fetching distribution data'));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchInitialDistributionData();

    const channel = supabase
      .channel('public:distribution_stats') // Unique channel name
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'distribution_stats', filter: 'id=eq.1' },
        (payload) => {
          console.log('Realtime update received for distribution_stats:', payload);
          if (payload.new && mounted) {
            setData(transformDbToDistributionData(payload.new as DbDistributionStats));
            setError(null);
            setLoading(false);
          }
        }
      )
      .subscribe((status, err) => {
        if (!mounted) return;
        if (err) {
          console.error('Realtime subscription error for distribution_stats:', err);
          setError(new Error('Realtime connection failed for distribution_stats: ' + err.message));
        } else if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to distribution_stats updates.');
        }
      });

    return () => {
      mounted = false;
      if (channel) {
        supabase.removeChannel(channel).catch(errRm => console.error("Failed to remove channel for distribution_stats", errRm));
      }
    };
  }, []);

  return { data, loading, error };
}